/* eslint-disable @reatom/unit-naming-rule */
import { noop } from '@reatom/framework'
import { useAction, useAtom, useCtx } from '@reatom/npm-react'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'
import { match } from 'ts-pattern'
import { useMemo } from 'use-memo-one'

import { AsyncStateN } from '@blade/std-lib'
import { selectedNetworkAtom } from '@root/entities/network'
import { reatomCreatorPairsStats } from '@root/features/services/creator-pairs/atoms/reatom-creator-pairs-stats'
import { reatomCreatorPairsTable } from '@root/features/services/creator-pairs/atoms/reatom-creator-pairs-table'
import { reatomPositionsTable } from '@root/features/trade/position/positions-table/atoms'
import { TradeHistoryTable } from '@root/features/trade/tables/trade-history-table'
import {
  tradeTablesScrollLockCss,
  useTradeTablesScrollLock,
} from '@root/features/trade/tables/use-trade-tables-scroll-lock'
import { mediaMaxDesktopSmall } from '@root/shared/ui-kit'
import { reatomOrdersTable } from '@root/widgets/trade/tables/orders-table/atoms'
import { OrdersTablesSkeleton } from '@root/widgets/trade/tables/orders-table/ui/orders-tables-skeleton'

import { tradeTableTabAtom } from '../../atoms/reatom-trading-tables'
import { TradeDevTokens } from '../trade-dev-tokens/trade-dev-tokens'
import { TradeHoldersTab } from '../trade-holders-tab/trade-holders-tab'

import { TradeOrdersTable } from './trade-orders-table'
import { TradePositionsTable } from './trade-positions-table'
import { TradeTablesActions } from './trade-tables-actions'
import { TradeTablesTabs } from './trade-tables-tabs'
import { TradeTopTradersTable } from './trade-top-traders-table'
import { TradesTable } from './trades-table'

import type { TradesTableModel } from '../../atoms/reatom-trades-table'
import type { PairAddress, PairModel } from '@root/entities/pair'
import type { ClassNameProps } from '@root/shared/react'

type TradeTablesProps = {
  pairAddress: PairAddress
  pair: AsyncStateN.AsyncStateN<unknown, PairModel>
  tradesTableModel: TradesTableModel | undefined
} & ClassNameProps

export const TradeTables = ({
  className,
  pair,
  pairAddress,
  tradesTableModel,
}: TradeTablesProps) => {
  const ctx = useCtx()
  const rootRef = useRef<HTMLDivElement>(null)

  const [tab] = useAtom(tradeTableTabAtom)
  const [network] = useAtom(selectedNetworkAtom)

  useTradeTablesScrollLock(rootRef, tab)

  const positionsTableModel = useMemo(
    () => reatomPositionsTable({ hiddenFilter: false }),
    [],
  )
  const initializePositionsTable = useAction(positionsTableModel.initialize)

  const ordersModel = useMemo(
    () => reatomOrdersTable(pairAddress),
    [pairAddress],
  )
  const [devAddress] = useAtom(
    (ctx) => {
      if (!pair.value) {
        return undefined
      }
      return ctx.spy(pair.value.initTxCreator)?.address
    },
    [pair],
  )

  const creatorPairsTableModel = useMemo(
    () =>
      devAddress
        ? reatomCreatorPairsTable({
            creatorAddress: devAddress,
            network: network,
            excludePair: pairAddress,
          })
        : undefined,
    [devAddress, network, pairAddress],
  )
  const creatorPairsOverviewModel = useMemo(
    () =>
      devAddress
        ? reatomCreatorPairsStats({
            creatorAddress: devAddress,
            network: network,
          })
        : undefined,
    [devAddress, network],
  )

  useEffect(() => {
    return initializePositionsTable()
  }, [initializePositionsTable])

  useEffect(() => {
    return ctx.subscribe(ordersModel, noop)
  }, [ctx, ordersModel])

  const renderContent = (pair: PairModel) => {
    return match(tab)
      .with('trades', () =>
        tradesTableModel ? (
          <TradesTable
            model={tradesTableModel}
            pair={pair}
            pairAddress={pair.address}
          />
        ) : undefined,
      )
      .with('orders', () => (
        <TradeOrdersTable model={ordersModel} pairAddress={pair.address} />
      ))
      .with('positions', () => (
        <TradePositionsTable model={positionsTableModel} />
      ))
      .with('history', () => <TradeHistoryTable pair={pair} />)
      .with('holders', () => <TradeHoldersTab pair={pair} />)
      .with('top-holders', () => <TradeTopTradersTable pair={pair} />)
      .with('dev-tokens', () => (
        <TradeDevTokens
          pair={pair}
          tableModel={creatorPairsTableModel}
          overviewModel={creatorPairsOverviewModel}
        />
      ))
      .exhaustive()
  }

  return (
    <Root ref={rootRef} className={className}>
      <Header>
        <TradeTablesTabs pair={pair} pairAddress={pairAddress} />
        <TradeTablesActions />
      </Header>
      <AnimatePresence initial={false}>
        <Content key={tab} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {AsyncStateN.isSuccess(pair) ? (
            renderContent(pair.value)
          ) : (
            <OrdersTablesSkeleton />
          )}
        </Content>
      </AnimatePresence>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  min-height: 0;
  position: relative;

  ${tradeTablesScrollLockCss}
`

const Content = styled(motion.div)`
  height: 100%;
  min-height: 0;
  display: grid;
`

const Header = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  border-bottom: 1px solid var(--background-3);
  padding-inline: 3px 0;

  ${mediaMaxDesktopSmall(css`
    padding-inline: 14px 0;
  `)}
`
